import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * W16-F4-S1 — Breadcrumbs trail component.
 *
 * Pre-localised: callers (consumers of `useBreadcrumbs()` from W16-F4-S2)
 * resolve i18n keys + dynamic loader/query data into ready-to-render
 * labels before passing them in. The primitive itself is route-agnostic.
 *
 * Truncation: labels longer than 30 chars are clipped to "first 27…" with
 * a tooltip exposing the full text on hover/focus.
 */

const MAX_LABEL_CHARS = 30;
const TRUNCATE_AT = 27;

export interface BreadcrumbItemData {
  label: string;
  to?: string;
  /**
   * Stable identifier for the segment (the route's pathname). Preserved
   * even when `to` is dropped on the last item — used by the W16-F4-S3
   * override mechanism to swap dynamic labels without re-resolving the
   * trail.
   */
  path?: string;
}

export interface BreadcrumbsProps {
  items: readonly BreadcrumbItemData[];
  className?: string;
}

function truncate(label: string): { display: string; truncated: boolean } {
  if (label.length <= MAX_LABEL_CHARS) {
    return { display: label, truncated: false };
  }
  return { display: `${label.slice(0, TRUNCATE_AT)}…`, truncated: true };
}

export function Breadcrumbs({
  items,
  className,
}: BreadcrumbsProps): JSX.Element | null {
  if (items.length === 0) return null;

  return (
    <Breadcrumb className={className}>
      <TooltipProvider delayDuration={200}>
        <BreadcrumbList>
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            const { display, truncated } = truncate(item.label);
            const labelEl = truncated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      'inline-block max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap align-middle',
                    )}
                  >
                    {display}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{item.label}</TooltipContent>
              </Tooltip>
            ) : (
              <span
                className={cn(
                  'inline-block max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap align-middle',
                )}
              >
                {display}
              </span>
            );

            return (
              <BreadcrumbItemWithSep
                key={`${item.to ?? 'page'}-${idx}`}
                showSeparator={idx > 0}
              >
                {isLast || !item.to ? (
                  <BreadcrumbPage>{labelEl}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.to}>{labelEl}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItemWithSep>
            );
          })}
        </BreadcrumbList>
      </TooltipProvider>
    </Breadcrumb>
  );
}

function BreadcrumbItemWithSep({
  showSeparator,
  children,
}: {
  showSeparator: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      {showSeparator ? <BreadcrumbSeparator /> : null}
      <BreadcrumbItem>{children}</BreadcrumbItem>
    </>
  );
}
